import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import TaxProfileSummaryCard from "@/components/TaxProfileSummaryCard";
import { UseTemplateButton } from "@/components/UseTemplateButton";
import { useCurrencyContext, equivalentFor } from "@/lib/currency";
import { FxDisclaimer } from "@/components/shared/DualPrice";
import { groupServices } from "@/utils/groupServices";

const money = (n, c = "USD") => `${c === "USD" ? "$" : ""}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}${c !== "USD" ? " " + c : ""}`;

const WORKFLOW_FIELDS = [
  ["default_engagement_letter", "Engagement Letter", "FileSignature"],
];

export default function EstimateGenerator() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientId, setClientId] = useState(searchParams.get("client") || "");
  const [serviceId, setServiceId] = useState("");
  const [addonCodes, setAddonCodes] = useState([]);
  const [qty, setQty] = useState(1);
  const [extras, setExtras] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/crm/contacts", { params: { limit: 500 } }),
      api.get("/sales/service-catalog"),
    ]).then(([c, s]) => {
      setClients(c.data.contacts || c.data || []);
      setServices(s.data.services || []);
      setCategories(s.data.categories || []);
    }).catch(() => toast.error("Failed to load generator data"));
  }, []);

  const svc = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);

  // When service changes, pre-select required add-ons and clear others.
  useEffect(() => {
    if (svc) setAddonCodes((svc.addons || []).filter((a) => a.required).map((a) => a.code));
    else setAddonCodes([]);
  }, [svc]);

  const toggleAddon = (a) => {
    if (a.required) return;
    setAddonCodes((p) => p.includes(a.code) ? p.filter((c) => c !== a.code) : [...p, a.code]);
  };

  const addExtra = (type) => setExtras((p) => [...p, { _uid: (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `x_${Date.now()}_${p.length}`), item_type: type, name: "", quantity: 1, unit_price: 0 }]);
  const updExtra = (i, k, v) => setExtras((p) => p.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const rmExtra = (i) => setExtras((p) => p.filter((_, idx) => idx !== i));

  const lines = useMemo(() => {
    if (!svc) return [];
    const out = [{ name: svc.name, qty: qty || 1, price: svc.base_price, kind: "service" }];
    (svc.addons || []).filter((a) => addonCodes.includes(a.code) || a.required).forEach((a) =>
      out.push({ name: a.name, qty: 1, price: a.price, kind: a.required ? "addon-req" : "addon" }));
    extras.filter((e) => e.name).forEach((e) =>
      out.push({ name: e.name, qty: e.quantity || 1, price: e.item_type === "discount" ? -Math.abs(e.unit_price) : e.unit_price, kind: e.item_type }));
    return out;
  }, [svc, qty, addonCodes, extras]);

  const total = useMemo(() => lines.reduce((s, l) => s + (l.qty * l.price), 0), [lines]);
  const fx = useCurrencyContext(clientId);
  const eq = (v) => equivalentFor(v, svc?.currency || "USD", { rate: fx.usd_thb_rate, showThb: fx.show_thb });

  const generate = async () => {
    if (!clientId) { toast.error("Select a client or lead"); return; }
    if (!serviceId) { toast.error("Select a primary service"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/sales/estimates/from-service", {
        client_id: clientId, service_id: serviceId, addon_codes: addonCodes, quantity: Number(qty) || 1,
        extra_line_items: extras.filter((e) => e.name).map((e) => ({ ...e, unit_price: Number(e.unit_price) || 0, quantity: Number(e.quantity) || 1 })),
      });
      toast.success(`${data.estimate_number} generated`);
      navigate(`/sales/estimates`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate estimate"); } finally { setSaving(false); }
  };

  const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200";

  return (
    <div className="p-6 max-w-[1280px] mx-auto" data-testid="estimate-generator">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/sales/estimates")} className="text-slate-400 hover:text-slate-700" data-testid="eg-back"><Icons.ArrowLeft className="h-5 w-5" /></button>
        <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center"><Icons.Calculator className="h-5 w-5 text-white" /></div>
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">Sales</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Estimate Generator</h1>
          <p className="text-sm text-slate-500">Pick a service — pricing, scope, team and downstream workflow are inherited from the Service Library.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LEFT — selections */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-heading font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2"><span className="h-6 w-6 rounded-md bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">1</span>Client / Lead</h3>
            <select className={inp} value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="eg-client">
              <option value="">Select a client or lead…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name} {c.email ? `· ${c.email}` : ""}</option>)}
            </select>
            <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
              <UseTemplateButton clientId={clientId} disabled={!clientId} variant="outline" label="Start from a Template"
                testid="eg-use-template" />
              <span className="text-[11px] text-slate-400">{clientId ? "One-click estimate from a saved template." : "Select a client to enable."}</span>
            </div>
          </div>

          {clientId && <TaxProfileSummaryCard clientId={clientId} />}

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-heading font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2"><span className="h-6 w-6 rounded-md bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">2</span>Primary Service</h3>
            <select className={inp} value={serviceId} onChange={(e) => setServiceId(e.target.value)} data-testid="eg-service">
              <option value="">Select a service…</option>
              {groupServices(services, categories).map(({ label, items }) => (
                <optgroup key={label} label={label}>
                  {items.map((s) => <option key={s.id} value={s.id}>{s.name} — {money(s.base_price, s.currency)}</option>)}
                </optgroup>
              ))}
            </select>
            {svc && (
              <div className="mt-3 flex items-center gap-3">
                <label className="text-xs text-slate-500">Quantity</label>
                <input type="number" min="1" className="w-20 border border-slate-300 rounded-lg px-2 py-1 text-sm" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="eg-qty" />
                <span className="text-xs text-slate-400">{svc.pricing_type} · {svc.currency}</span>
              </div>
            )}
          </div>

          {svc && (svc.addons || []).length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="eg-addons">
              <h3 className="font-heading font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2"><span className="h-6 w-6 rounded-md bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">3</span>Add-Ons</h3>
              <div className="space-y-2">
                {(svc.addons || []).map((a) => {
                  const on = a.required || addonCodes.includes(a.code);
                  return (
                    <label key={a.code} data-testid={`eg-addon-${a.code}`} className={`flex items-center justify-between rounded-lg border px-3 py-2.5 cursor-pointer ${on ? "border-blue-300 bg-blue-50/50" : "border-slate-200"} ${a.required ? "opacity-90 cursor-default" : ""}`}>
                      <div className="flex items-center gap-2.5">
                        <input type="checkbox" checked={on} disabled={a.required} onChange={() => toggleAddon(a)} className="accent-blue-600" />
                        <div>
                          <div className="text-sm text-slate-700">{a.name} {a.required && <span className="text-[10px] text-rose-600 ml-1">Required</span>}</div>
                          <div className="text-[11px] text-slate-400">{a.category || a.code}</div>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-slate-700">{money(a.price, a.currency)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {svc && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-slate-800 text-sm flex items-center gap-2"><span className="h-6 w-6 rounded-md bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center">4</span>Optional Line Items</h3>
                <div className="flex gap-1.5">
                  {[["custom_fee", "Fee"], ["discount", "Discount"], ["adjustment", "Adjustment"]].map(([t, l]) => (
                    <button key={t} onClick={() => addExtra(t)} data-testid={`eg-add-${t}`} className="text-[11px] border border-slate-200 rounded-lg px-2 py-1 text-slate-600 hover:bg-slate-50">+ {l}</button>
                  ))}
                </div>
              </div>
              {extras.length === 0 ? <p className="text-xs text-slate-400">None. Add one-time fees, discounts or adjustments if needed.</p> : (
                <div className="space-y-2">
                  {extras.map((e, i) => (
                    <div key={e._uid || i} className="flex items-center gap-2" data-testid={`eg-extra-${i}`}>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 capitalize w-20 text-center">{e.item_type.replace("_", " ")}</span>
                      <input className="flex-1 border border-slate-300 rounded-lg px-2 py-1 text-sm" placeholder="Description" value={e.name} onChange={(ev) => updExtra(i, "name", ev.target.value)} />
                      <input type="number" className="w-24 border border-slate-300 rounded-lg px-2 py-1 text-sm" placeholder="Amount" value={e.unit_price} onChange={(ev) => updExtra(i, "unit_price", ev.target.value)} />
                      <button onClick={() => rmExtra(i)} className="text-slate-400 hover:text-rose-600"><Icons.X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT — inherited config preview */}
        <div className="space-y-4">
          {!svc ? (
            <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-400">
              <Icons.BookOpen className="h-10 w-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Select a service to preview the configuration inherited from the Service Library.</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="eg-preview">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold text-slate-800">{svc.name}</h3>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{svc.code}</span>
                </div>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                  <Field label="Assigned Team" value={svc.assigned_team_name} icon="Users" />
                  <Field label="Engagement Type" value={svc.engagement_type} icon="Briefcase" />
                  <Field label="Billing Rule" value={svc.billing_rule_label} icon="Receipt" />
                  <Field label="Payment Rule" value={svc.payment_rule_label} icon="CreditCard" />
                  <Field label="Turnaround" value={svc.typical_turnaround} icon="Clock" />
                  <Field label="Currency" value={svc.currency} icon="DollarSign" />
                </div>
                {svc.client_description && <Block label="Client Description" text={svc.client_description} />}
                {svc.default_scope && <Block label="Scope of Work" text={svc.default_scope} />}
                {svc.exclusions && <Block label="Exclusions" text={svc.exclusions} />}
                {svc.deliverables && <Block label="Deliverables" text={svc.deliverables} />}
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="eg-workflow">
                <h3 className="font-heading font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2"><Icons.FileSignature className="h-4 w-4 text-blue-500" />Engagement Letter</h3>
                <p className="text-[11px] text-slate-400 mb-3">Captured into this estimate; used to generate the engagement letter when accepted.</p>
                <div className="grid grid-cols-1 gap-2">
                  {WORKFLOW_FIELDS.map(([k, label, icon]) => {
                    const Ic = Icons[icon] || Icons.Circle;
                    const v = svc[k];
                    return (
                      <div key={k} className="flex items-center gap-2 text-sm border border-slate-100 rounded-lg px-2.5 py-2">
                        <Ic className={`h-4 w-4 shrink-0 ${v ? "text-blue-500" : "text-slate-300"}`} />
                        <div className="min-w-0"><div className="text-[10px] text-slate-400">{label}</div><div className={`truncate ${v ? "text-slate-700" : "text-slate-300 italic"}`}>{v || "Not set"}</div></div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="eg-pricing">
                <h3 className="font-heading font-semibold text-slate-800 text-sm mb-3 flex items-center gap-2"><Icons.Calculator className="h-4 w-4 text-blue-500" />Pricing</h3>
                <div className="space-y-1.5">
                  {lines.map((l, i) => (
                    <div key={i} className="flex items-start justify-between text-sm">
                      <span className="text-slate-600">{l.name}{l.qty > 1 ? ` ×${l.qty}` : ""}{l.kind === "addon-req" ? " (required)" : ""}</span>
                      <span className="inline-flex flex-col items-end leading-tight"><span className={l.price < 0 ? "text-rose-600" : "text-slate-700"}>{money(l.qty * l.price, svc.currency)}</span>{eq(l.qty * l.price) && <span className="text-[11px] text-slate-400">≈ {eq(l.qty * l.price)}</span>}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-start justify-between border-t border-slate-100 mt-3 pt-3">
                  <span className="font-semibold text-slate-800">Estimated Total</span>
                  <span className="inline-flex flex-col items-end leading-tight"><span className="font-heading font-bold text-xl text-slate-900" data-testid="eg-total">{money(total, svc.currency)}</span>{eq(total) && <span className="text-[11px] text-slate-400 font-normal">≈ {eq(total)}</span>}</span>
                </div>
                {eq(total) && <FxDisclaimer className="mt-2 text-right" />}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-6 sticky bottom-0 bg-slate-50 py-3 border-t border-slate-200">
        <button onClick={() => navigate("/sales/estimates")} className="text-sm border border-slate-200 rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={generate} disabled={saving || !clientId || !serviceId} data-testid="eg-generate" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-50">{saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}Save as Draft</button>
      </div>
    </div>
  );
}

function Field({ label, value, icon }) {
  const Ic = Icons[icon] || Icons.Circle;
  return (
    <div className="flex items-center gap-2">
      <Ic className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="min-w-0"><div className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</div><div className="text-slate-700 truncate">{value || "—"}</div></div>
    </div>
  );
}
function Block({ label, text }) {
  return (
    <div className="mt-3">
      <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">{label}</div>
      <p className="text-sm text-slate-600 whitespace-pre-wrap">{text}</p>
    </div>
  );
}
